import {
	registerStep1Schema,
	registerStep2Schema,
	registerStep3Schema,
	registerStep4Schema,
} from "@shared/schemas";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Loader2, Mail, RefreshCw, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useOAuthRegister, useRegister } from "@/api/mutations/register";
import { usePublicConfig } from "@/api/queries/config";
import { useValidateEmail, useValidateUsername } from "@/api/queries/register";
import {
	FormDivider,
	GenderSelector,
	IntoSelector,
	OAuthButtons,
	PlacesAutocomplete,
	StepIndicator,
} from "@/components/auth";
import { PasswordInput } from "@/components/auth/PasswordInput";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Spinner } from "@/components/ui/Spinner";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { usePageMeta } from "@/hooks/use-page-meta";
import { ApiError, api } from "@/lib/api-client";
import { zodResolver } from "@/lib/zod-resolver";
import { useToastStore } from "@/stores/toast-store";

export const Route = createFileRoute("/_auth/register")({
	component: RegisterPage,
});

interface WizardData {
	gender?: "m" | "f";
	into?: "m" | "f" | "m+f";
	username?: string;
	dob?: string;
	place?: number;
	placeName?: string;
	email?: string;
	password?: string;
	agb?: boolean;
}

const fieldToStep: Record<string, number> = {
	gender: 1,
	into: 1,
	username: 2,
	dob: 2,
	place: 3,
	email: 4,
	password: 4,
};

const SESSION_KEY = "register-wizard";

interface SavedWizardState {
	step: number;
	wizardData: WizardData;
	email?: string;
}

function saveWizardState(step: number, wizardData: WizardData) {
	try {
		const state: SavedWizardState = { step, wizardData, email: wizardData.email };
		sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
	} catch {
		// sessionStorage nicht verfügbar
	}
}

function loadWizardState(): SavedWizardState | null {
	try {
		const stored = sessionStorage.getItem(SESSION_KEY);
		if (!stored) return null;
		return JSON.parse(stored) as SavedWizardState;
	} catch {
		return null;
	}
}

function clearWizardState() {
	try {
		sessionStorage.removeItem(SESSION_KEY);
	} catch {
		// sessionStorage nicht verfügbar
	}
}

function RegisterPage() {
	const { addToast } = useToastStore();
	const { data: config } = usePublicConfig();

	usePageMeta({
		title: config ? `Registrieren - ${config.seo.defaultTitle}` : undefined,
		noIndex: true,
	});

	// Lade gespeicherten State aus sessionStorage
	const savedState = loadWizardState();
	// Step 5 ist nicht wiederherstellbar (Mutation-State geht bei Refresh verloren)
	const restoredStep = savedState?.step === 5 ? 1 : (savedState?.step ?? 1);

	const [wizardData, setWizardData] = useState<WizardData>(
		restoredStep === 1 && savedState?.step === 5 ? {} : (savedState?.wizardData ?? {}),
	);
	const [currentStep, setCurrentStep] = useState(restoredStep);
	const [submitError, setSubmitError] = useState<string | null>(null);

	const registerMutation = useRegister();
	const oauthRegisterMutation = useOAuthRegister();
	const oauthProviders = config?.oauthProviders ?? [];

	const updateWizardData = useCallback((updates: Partial<WizardData>) => {
		setWizardData((prev) => {
			const next = { ...prev, ...updates };
			return next;
		});
	}, []);

	function goNext() {
		setCurrentStep((s) => {
			const next = s + 1;
			saveWizardState(next, wizardData);
			return next;
		});
	}

	function goBack() {
		setCurrentStep((s) => {
			const next = s - 1;
			saveWizardState(next, wizardData);
			return next;
		});
	}

	async function handleFinalSubmit(step4Data: Partial<WizardData>) {
		const finalData = { ...wizardData, ...step4Data };
		setWizardData(finalData);
		setCurrentStep(5);
		saveWizardState(5, finalData);
		setSubmitError(null);

		try {
			await registerMutation.mutateAsync({
				email: finalData.email ?? "",
				password: finalData.password ?? "",
				username: finalData.username ?? "",
				gender: finalData.gender ?? "m",
				into: finalData.into ?? "m",
				dob: finalData.dob ?? "",
				place: finalData.place ?? 0,
				agb: true,
			});
		} catch (error) {
			if (error instanceof ApiError && error.isValidationError) {
				const fieldErrors = error.fieldErrors;
				const firstField = Object.keys(fieldErrors)[0];
				if (firstField && fieldToStep[firstField]) {
					setCurrentStep(fieldToStep[firstField]);
					setSubmitError(Object.values(fieldErrors).flat()[0] ?? error.message);
					return;
				}
			}
			const message = error instanceof ApiError ? error.message : "Registrierung fehlgeschlagen.";
			setCurrentStep(4);
			setSubmitError(message);
		}
	}

	function handleOAuthRegister(provider: string) {
		oauthRegisterMutation.mutate(provider, {
			onSuccess: (data) => {
				if (data.redirectUrl) {
					window.location.href = data.redirectUrl;
				}
			},
			onError: () => {
				addToast({
					type: "error",
					message: "OAuth-Registrierung konnte nicht gestartet werden.",
				});
			},
		});
	}

	return (
		<div className="space-y-6">
			{currentStep <= 4 && (
				<div className="text-center">
					<h1 className="text-2xl font-bold text-text">Erstelle deinen Account</h1>
				</div>
			)}

			{currentStep <= 4 && <StepIndicator currentStep={currentStep} totalSteps={4} />}

			{submitError && currentStep <= 4 && (
				<p className="text-error text-base text-center" role="alert">
					{submitError}
				</p>
			)}

			{currentStep === 1 && (
				<Step1
					wizardData={wizardData}
					onUpdate={updateWizardData}
					onNext={goNext}
					oauthProviders={oauthProviders}
					onOAuthRegister={handleOAuthRegister}
					oauthLoading={oauthRegisterMutation.isPending ? oauthRegisterMutation.variables : null}
				/>
			)}
			{currentStep === 2 && (
				<Step2
					wizardData={wizardData}
					onUpdate={updateWizardData}
					onNext={goNext}
					onBack={goBack}
				/>
			)}
			{currentStep === 3 && (
				<Step3
					wizardData={wizardData}
					onUpdate={updateWizardData}
					onNext={goNext}
					onBack={goBack}
				/>
			)}
			{currentStep === 4 && (
				<Step4
					wizardData={wizardData}
					onSubmit={handleFinalSubmit}
					onBack={goBack}
					isSubmitting={registerMutation.isPending}
				/>
			)}
			{currentStep === 5 && (
				<Step5
					isLoading={registerMutation.isPending}
					isSuccess={registerMutation.isSuccess}
					email={wizardData.email ?? ""}
				/>
			)}

			{currentStep === 1 && (
				<div className="text-center">
					<span className="text-base text-text-secondary">Bereits einen Account? </span>
					<Link to="/login" className="text-base text-primary hover:underline">
						Jetzt einloggen
					</Link>
				</div>
			)}
		</div>
	);
}

// -- Step 1: Gender + Into --

interface Step1Props {
	wizardData: WizardData;
	onUpdate: (data: Partial<WizardData>) => void;
	onNext: () => void;
	oauthProviders: string[];
	onOAuthRegister: (provider: string) => void;
	oauthLoading: string | null;
}

function Step1({
	wizardData,
	onUpdate,
	onNext,
	oauthProviders,
	onOAuthRegister,
	oauthLoading,
}: Step1Props) {
	const [errors, setErrors] = useState<Record<string, string>>({});

	function handleNext() {
		const result = registerStep1Schema.safeParse({
			gender: wizardData.gender,
			into: wizardData.into,
		});

		if (!result.success) {
			const newErrors: Record<string, string> = {};
			for (const issue of result.error.issues) {
				const path = issue.path.join(".");
				if (!newErrors[path]) newErrors[path] = issue.message;
			}
			setErrors(newErrors);
			return;
		}

		setErrors({});
		onNext();
	}

	return (
		<div className="space-y-4">
			<GenderSelector
				value={wizardData.gender ?? null}
				onChange={(v) => {
					onUpdate({ gender: v });
					setErrors((prev) => ({ ...prev, gender: "" }));
				}}
				error={errors.gender}
			/>

			<IntoSelector
				value={wizardData.into ?? null}
				onChange={(v) => {
					onUpdate({ into: v });
					setErrors((prev) => ({ ...prev, into: "" }));
				}}
				error={errors.into}
			/>

			{oauthProviders.length > 0 && (
				<>
					<FormDivider text="Schneller mit" />
					<OAuthButtons
						mode="register"
						providers={oauthProviders}
						onProviderClick={onOAuthRegister}
						loading={oauthLoading}
					/>
				</>
			)}

			<Button type="button" variant="primary" fullWidth onClick={handleNext}>
				Weiter
			</Button>
		</div>
	);
}

// -- Step 2: Username + DOB --

interface Step2Props {
	wizardData: WizardData;
	onUpdate: (data: Partial<WizardData>) => void;
	onNext: () => void;
	onBack: () => void;
}

function Step2({ wizardData, onUpdate, onNext, onBack }: Step2Props) {
	const [username, setUsername] = useState(wizardData.username ?? "");
	const debouncedUsername = useDebouncedValue(username, 500);
	const usernameValidation = useValidateUsername(debouncedUsername);

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		resolver: zodResolver(registerStep2Schema),
		defaultValues: {
			username: wizardData.username ?? "",
			dob: wizardData.dob ?? "",
		},
	});

	const isUsernameAvailable = usernameValidation.data?.status === "success";
	const isUsernameTaken = usernameValidation.data?.status === "error";
	const isUsernameChecking = usernameValidation.isFetching;

	function onSubmit(data: { username: string; dob: string }) {
		if (isUsernameTaken) return;
		if (isUsernameChecking) return;

		onUpdate({ username: data.username, dob: data.dob });
		onNext();
	}

	function renderUsernameStatus() {
		if (username.length < 4) return null;

		if (isUsernameChecking) {
			return (
				<span className="inline-flex items-center gap-1 text-xs text-text-muted">
					<Loader2 size={14} className="animate-spin" /> Prüfe...
				</span>
			);
		}
		if (isUsernameAvailable) {
			return (
				<span className="inline-flex items-center gap-1 text-xs text-success">
					<Check size={14} /> Verfügbar
				</span>
			);
		}
		if (isUsernameTaken) {
			return (
				<span className="inline-flex items-center gap-1 text-xs text-error">
					<X size={14} /> Vergeben
				</span>
			);
		}
		return null;
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
			<div>
				<div className="flex items-center justify-between mb-1">
					<label htmlFor="reg-username" className="block text-base font-medium text-text">
						Benutzername
					</label>
					{renderUsernameStatus()}
				</div>
				<input
					id="reg-username"
					type="text"
					placeholder="Dein Benutzername"
					autoComplete="username"
					aria-invalid={errors.username || isUsernameTaken ? true : undefined}
					className={`
						block w-full rounded-lg border bg-surface px-3 h-10 text-base
						text-text placeholder:text-text-muted
						focus-ring transition-colors
						${errors.username || isUsernameTaken ? "border-error" : "border-border hover:border-primary/50"}
					`}
					{...register("username", {
						onChange: (e) => setUsername(e.target.value),
					})}
				/>
				{errors.username && (
					<p className="mt-1 text-base text-error" role="alert">
						{errors.username.message}
					</p>
				)}
				{isUsernameTaken && !errors.username && (
					<p className="mt-1 text-base text-error" role="alert">
						Dieser Benutzername ist bereits vergeben
					</p>
				)}
			</div>

			<Input
				{...register("dob")}
				type="date"
				label="Geburtsdatum"
				error={errors.dob?.message}
				fullWidth
			/>

			<div className="flex gap-3">
				<Button type="button" variant="ghost" onClick={onBack} className="flex-1">
					Zurück
				</Button>
				<Button type="submit" variant="primary" className="flex-1">
					Weiter
				</Button>
			</div>
		</form>
	);
}

// -- Step 3: Location --

interface Step3Props {
	wizardData: WizardData;
	onUpdate: (data: Partial<WizardData>) => void;
	onNext: () => void;
	onBack: () => void;
}

function Step3({ wizardData, onUpdate, onNext, onBack }: Step3Props) {
	const [placeValue, setPlaceValue] = useState<{ id: number; name: string } | null>(
		wizardData.place && wizardData.placeName
			? { id: wizardData.place, name: wizardData.placeName }
			: null,
	);
	const [error, setError] = useState<string>("");

	function handleNext() {
		const result = registerStep3Schema.safeParse({
			place: placeValue?.id,
			placeName: placeValue?.name,
		});

		if (!result.success) {
			setError(result.error.issues[0]?.message ?? "Bitte wähle einen Ort");
			return;
		}

		setError("");
		if (placeValue) {
			onUpdate({ place: placeValue.id, placeName: placeValue.name });
		}
		onNext();
	}

	return (
		<div className="space-y-4">
			<PlacesAutocomplete
				value={placeValue}
				onChange={(place) => {
					setPlaceValue(place);
					if (place) setError("");
				}}
				error={error}
			/>

			<div className="flex gap-3">
				<Button type="button" variant="ghost" onClick={onBack} className="flex-1">
					Zurück
				</Button>
				<Button type="button" variant="primary" onClick={handleNext} className="flex-1">
					Weiter
				</Button>
			</div>
		</div>
	);
}

// -- Step 4: Email + Password + AGB --

interface Step4Props {
	wizardData: WizardData;
	onSubmit: (data: Partial<WizardData>) => void;
	onBack: () => void;
	isSubmitting: boolean;
}

function Step4({ wizardData, onSubmit, onBack, isSubmitting }: Step4Props) {
	const [email, setEmail] = useState(wizardData.email ?? "");
	const debouncedEmail = useDebouncedValue(email, 500);
	const emailValidation = useValidateEmail(debouncedEmail);

	const isEmailAvailable = emailValidation.data?.status === "success";
	const isEmailTaken = emailValidation.data?.status === "error";
	const isEmailChecking = emailValidation.isFetching;

	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		watch,
	} = useForm<{ email: string; password: string; agb: boolean }>({
		resolver: zodResolver(registerStep4Schema) as Resolver<{
			email: string;
			password: string;
			agb: boolean;
		}>,
		defaultValues: {
			email: wizardData.email ?? "",
			password: wizardData.password ?? "",
			agb: wizardData.agb ?? false,
		},
	});

	const agbValue = watch("agb");

	function handleFormSubmit(data: { email: string; password: string; agb: boolean }) {
		if (isEmailTaken) return;
		if (isEmailChecking) return;

		onSubmit({ email: data.email, password: data.password, agb: data.agb as true });
	}

	function renderEmailStatus() {
		if (!email || email.length < 5 || !email.includes("@")) return null;

		if (isEmailChecking) {
			return (
				<span className="inline-flex items-center gap-1 text-xs text-text-muted">
					<Loader2 size={14} className="animate-spin" /> Prüfe...
				</span>
			);
		}
		if (isEmailAvailable) {
			return (
				<span className="inline-flex items-center gap-1 text-xs text-success">
					<Check size={14} /> Verfügbar
				</span>
			);
		}
		if (isEmailTaken) {
			return (
				<span className="inline-flex items-center gap-1 text-xs text-error">
					<X size={14} /> Bereits registriert
				</span>
			);
		}
		return null;
	}

	return (
		<form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4" noValidate>
			<div>
				<div className="flex items-center justify-between mb-1">
					<label htmlFor="reg-email" className="block text-base font-medium text-text">
						E-Mail
					</label>
					{renderEmailStatus()}
				</div>
				<input
					id="reg-email"
					type="email"
					placeholder="deine@email.de"
					autoComplete="email"
					aria-invalid={errors.email || isEmailTaken ? true : undefined}
					className={`
						block w-full rounded-lg border bg-surface px-3 h-10 text-base
						text-text placeholder:text-text-muted
						focus-ring transition-colors
						${errors.email || isEmailTaken ? "border-error" : "border-border hover:border-primary/50"}
					`}
					{...register("email", {
						onChange: (e) => setEmail(e.target.value),
					})}
				/>
				{errors.email && (
					<p className="mt-1 text-base text-error" role="alert">
						{errors.email.message}
					</p>
				)}
				{isEmailTaken && !errors.email && (
					<p className="mt-1 text-base text-error" role="alert">
						Diese E-Mail ist bereits registriert
					</p>
				)}
			</div>

			<PasswordInput
				{...register("password")}
				label="Passwort"
				placeholder="Mindestens 6 Zeichen"
				error={errors.password?.message}
				autoComplete="new-password"
				showStrength
				fullWidth
			/>

			<label className="flex items-start gap-3 cursor-pointer">
				<input
					type="checkbox"
					checked={agbValue === true}
					onChange={(e) =>
						setValue("agb", e.target.checked as unknown as true, { shouldValidate: true })
					}
					className="mt-0.5 h-4 w-4 rounded border-border bg-surface text-primary focus-ring cursor-pointer accent-primary"
				/>
				<span className="text-base text-text-secondary">
					Ich akzeptiere die{" "}
					<a
						href="/agb"
						target="_blank"
						rel="noopener noreferrer"
						className="text-primary hover:underline"
					>
						AGB
					</a>
				</span>
			</label>
			{errors.agb && (
				<p className="text-error text-base" role="alert">
					{errors.agb.message}
				</p>
			)}

			<div className="flex gap-3">
				<Button type="button" variant="ghost" onClick={onBack} className="flex-1">
					Zurück
				</Button>
				<Button type="submit" variant="primary" className="flex-1" loading={isSubmitting}>
					Registrieren
				</Button>
			</div>
		</form>
	);
}

// -- Step 5: Verification Waiting --

const VERIFY_POLL_INITIAL = 3000;
const VERIFY_POLL_MAX = 30000;
const VERIFY_POLL_TIMEOUT = 5 * 60 * 1000; // 5 Minuten

interface Step5Props {
	isLoading: boolean;
	isSuccess: boolean;
	email: string;
}

function Step5({ isLoading, isSuccess, email }: Step5Props) {
	const [newEmail, setNewEmail] = useState("");
	const [emailChanged, setEmailChanged] = useState(false);
	const [verified, setVerified] = useState(false);
	const [pollTimedOut, setPollTimedOut] = useState(false);
	const [resendSuccess, setResendSuccess] = useState(false);

	const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pollStartRef = useRef(0);
	const currentIntervalRef = useRef(VERIFY_POLL_INITIAL);
	const hasPollStarted = useRef(false);

	// E-Mail ändern
	const changeEmailMutation = useMutation({
		mutationFn: async (emailToChange: string) => {
			return api.post<{ status: string; message?: string }>("/api/v3/register/changeEmail", {
				email: emailToChange,
			});
		},
		onSuccess: () => {
			setEmailChanged(true);
		},
	});

	// E-Mail erneut senden (nutzt gleichen Register-Endpoint -- falls vorhanden)
	const resendMutation = useMutation({
		mutationFn: async () => {
			return api.post<{ status: string; message?: string }>(
				"/api/v3/register/resend-verification",
				{ email },
			);
		},
		onSuccess: () => {
			setResendSuccess(true);
			// Reset nach 5 Sekunden für erneutes Senden
			setTimeout(() => setResendSuccess(false), 5000);
		},
	});

	// Polling für Verifizierungsstatus
	const checkVerification = useCallback(async () => {
		const elapsed = Date.now() - pollStartRef.current;
		if (elapsed >= VERIFY_POLL_TIMEOUT) {
			if (pollRef.current) {
				clearTimeout(pollRef.current);
				pollRef.current = null;
			}
			setPollTimedOut(true);
			return;
		}

		try {
			const result = await api.get<{ verified?: boolean; status?: string }>(
				"/api/v3/register/register",
			);
			if (result.verified || result.status === "verified") {
				if (pollRef.current) {
					clearTimeout(pollRef.current);
					pollRef.current = null;
				}
				setVerified(true);
				clearWizardState();
				return;
			}
		} catch {
			// Endpoint nicht verfügbar oder Fehler -- weiter polling
		}

		currentIntervalRef.current = Math.min(currentIntervalRef.current * 2, VERIFY_POLL_MAX);
		pollRef.current = setTimeout(checkVerification, currentIntervalRef.current);
	}, []);

	// Polling starten wenn Registrierung erfolgreich
	useEffect(() => {
		if (isSuccess && !hasPollStarted.current) {
			hasPollStarted.current = true;
			pollStartRef.current = Date.now();
			currentIntervalRef.current = VERIFY_POLL_INITIAL;
			pollRef.current = setTimeout(checkVerification, VERIFY_POLL_INITIAL);
		}

		return () => {
			if (pollRef.current) {
				clearTimeout(pollRef.current);
			}
		};
	}, [isSuccess, checkVerification]);

	if (isLoading) {
		return (
			<div className="flex flex-col items-center gap-4 py-8">
				<Spinner size="lg" label="Registrierung wird verarbeitet" />
				<p className="text-text-secondary text-base">Einen Moment bitte...</p>
			</div>
		);
	}

	if (verified) {
		return (
			<div className="flex flex-col items-center gap-4 py-8 text-center">
				<div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/20">
					<Check size={32} className="text-success" />
				</div>
				<h2 className="text-xl font-bold text-text">E-Mail bestätigt!</h2>
				<p className="text-base text-text-secondary max-w-xs">
					Deine E-Mail-Adresse wurde erfolgreich verifiziert. Du kannst dich jetzt anmelden.
				</p>
				<Link to="/login">
					<Button variant="primary">Zum Login</Button>
				</Link>
			</div>
		);
	}

	if (isSuccess) {
		return (
			<div className="flex flex-col items-center gap-6 py-8 text-center">
				<div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20">
					<Mail size={32} className="text-primary" />
				</div>
				<div>
					<h2 className="text-xl font-bold text-text">Fast geschafft!</h2>
					<p className="text-base text-text-secondary max-w-xs mt-2">
						Wir haben dir eine E-Mail an <span className="font-medium text-text">{email}</span>{" "}
						geschickt. Prüfe dein Postfach und bestätige deine E-Mail-Adresse.
					</p>
				</div>

				{!pollTimedOut && <Spinner size="sm" label="Warte auf Verifizierung" />}

				{pollTimedOut && (
					<p className="text-base text-text-muted">
						Automatische Prüfung beendet. Bitte prüfe dein Postfach manuell.
					</p>
				)}

				{/* E-Mail erneut senden */}
				<Button
					variant="ghost"
					size="sm"
					onClick={() => resendMutation.mutate()}
					disabled={resendMutation.isPending || resendSuccess}
					leftIcon={<RefreshCw size={16} />}
				>
					{resendSuccess ? "E-Mail gesendet" : "E-Mail erneut senden"}
				</Button>
				{resendMutation.isError && (
					<p className="text-base text-error">
						{resendMutation.error instanceof ApiError
							? resendMutation.error.message
							: "E-Mail konnte nicht erneut gesendet werden."}
					</p>
				)}

				{/* E-Mail ändern (einmalig) */}
				<div className="w-full max-w-xs border-t border-border pt-4">
					<p className="text-base text-text-secondary mb-3">
						Falsche E-Mail-Adresse? Du kannst sie einmalig ändern.
					</p>
					<div className="flex gap-2">
						<Input
							type="email"
							placeholder="Neue E-Mail-Adresse"
							value={newEmail}
							onChange={(e) => setNewEmail(e.target.value)}
							disabled={emailChanged}
							size="sm"
							fullWidth
						/>
						<Button
							variant="outline"
							size="sm"
							onClick={() => changeEmailMutation.mutate(newEmail.trim())}
							disabled={emailChanged || !newEmail.trim()}
							loading={changeEmailMutation.isPending}
							className="shrink-0"
						>
							{emailChanged ? "Geändert" : "Ändern"}
						</Button>
					</div>
					{changeEmailMutation.isError && (
						<p className="mt-2 text-base text-error">
							{changeEmailMutation.error instanceof ApiError
								? changeEmailMutation.error.message
								: "E-Mail konnte nicht geändert werden."}
						</p>
					)}
					{emailChanged && (
						<p className="mt-2 text-base text-success">
							E-Mail-Adresse wurde erfolgreich geändert.
						</p>
					)}
				</div>

				<Link to="/login" className="text-primary hover:underline text-base font-medium mt-2">
					Zum Login
				</Link>
			</div>
		);
	}

	// Fallback (sollte nicht erreicht werden)
	return null;
}
